import moment from "moment";

import { vocabularize } from "src/Common/vocab";

import { branchesFor, breakdownRows, caveatsFor } from "./breakdown";
import { ALL_PAY_RULES, formatHhMm, hasOvertimeRule,
  isManualEntry, isRuleOn,
  onCallDaysInRange,
  displayDay,
  signoffWeeksFor,
  byFirstPunch,
} from "./hours";

/**
 * The per-technician breakdowns as a printable document — one employee per
 * page, opened in its own window for the browser's print dialog.
 *
 * Built as a self-contained HTML document rather than as part of the app:
 *
 * - Printing from inside the page means hiding the whole application behind
 *   `@media print` rules and hoping nothing in the layout leaks onto the paper.
 *   A separate document has nothing to hide, so what is on screen in that window
 *   is exactly what comes out of the printer.
 * - Text stays text. The existing PDF path (`WO/Components/forms/formPdf.js`)
 *   rasterises HTML into jsPDF, which is right for arbitrary form schemas and
 *   wrong for a table of numbers — a payroll figure that has to be read off
 *   paper and re-keyed should not be a JPEG of a figure.
 * - The print dialog's "Save as PDF" gives a digital copy from the same action,
 *   so no separate export is needed to keep one.
 *
 * Deliberately no signature line. The design treats authorisation as the
 * signature and captures who and when, and the weekend overtime rule is still
 * unresolved — printing a sheet with somewhere to sign would have people
 * signing figures we already know may be recalculated. See DESIGN.md §5 and §6b.
 */

const escapeHtml = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// An empty bucket prints as an em dash rather than as blank space: on paper a
// blank cell and a cell nobody filled in look the same, and a timecard is read
// by someone checking that every line was accounted for.
const cell = (value) => (value ? escapeHtml(value) : "&mdash;");

/**
 * What a saved PDF is called. Chrome's "Save as PDF" takes the page title as
 * the file name, so each technician's sheet carries one: their name first, so
 * a folder of them sorts by person, then the dates in a form that sorts.
 */
const fileTitleFor = (technician, range) => {
  const dates =
    range && range[0] && range[1]
      ? ` ${moment(range[0]).format("YYYY-MM-DD")} to ${moment(range[1]).format("YYYY-MM-DD")}`
      : "";
  // Characters a file name cannot hold on some systems are dropped rather
  // than left for the browser to replace unpredictably.
  return `${String(technician.technician || "Technician").replace(/[\\/:*?"<>|]/g, "")} - Timecard${dates}`;
};

const rangeLabel = (range) =>
  range && range[0] && range[1]
    ? `${moment(range[0]).format("MMM D, YYYY")} – ${moment(range[1]).format("MMM D, YYYY")}`
    : "All loaded hours";

const minutesOf = (value) => {
  if (typeof value !== "string" || !/^\d{1,2}:\d{2}$/.test(value)) return 0;
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
};
const entryMinutes = (entry) => minutesOf(entry?.regularTime) + minutesOf(entry?.travelTime);
const hoursText = (minutes) => {
  const hours = Math.round((minutes / 60) * 100) / 100;
  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
};
const headlineFor = (event) => {
  if (event.kind === "ENTRY_ADDED") return `Logged ${hoursText(entryMinutes(event.after))}`;
  if (event.kind === "ENTRY_REMOVED") return `Removed ${hoursText(entryMinutes(event.before))}`;
  return event.description || "";
};

/**
 * Changes made to this technician's hours from Time Management, for the days
 * on the sheet. Only that source: a sheet filed in a cabinet is the record of
 * what payroll did to the figures, and a change someone made from the app is
 * already theirs. Each line carries who, when, and the reason they gave.
 */
const changesBlock = (events, range) => {
  const inRange = (event) =>
    !range?.[0] || !range?.[1] || !event.date
      ? true
      : !moment(event.date).isBefore(moment(range[0]), "day") &&
        !moment(event.date).isAfter(moment(range[1]), "day");
  const rows = (events || [])
    .filter((event) => event.source === "PAYROLL" && inRange(event))
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  if (!rows.length) return "";
  return `
      <div class="changes">
        <h2>Changes made from Time Management</h2>
        <table class="changes-table">
          <thead>
            <tr><th>Day</th><th>Change</th><th>WO #</th><th>By</th><th>Reason</th></tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (event) => `
            <tr>
              <td class="date">${event.date ? displayDay(moment(event.date).format("YYYY-MM-DD")) : "&mdash;"}</td>
              <td>${escapeHtml(headlineFor(event))}</td>
              <td class="wo">${cell(event.woNumber)}</td>
              <td class="by">${escapeHtml(event.actorName || "Unknown user")}<br /><span class="when">${moment(
                  event.createdAt
                ).format("MMM D, YYYY h:mm A")}</span></td>
              <td class="reason">${event.reason ? escapeHtml(event.reason) : "&mdash;"}</td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table>
      </div>`;
};

/**
 * The signature line, digital: for each finished week on the sheet, whether
 * the technician signed it and when — or that the office did, and why. This
 * is what the sheet deliberately left blank while sign-off was on paper.
 */
const signoffBlock = (technician, range, signoff, events) => {
  const weeks = signoffWeeksFor(signoff, range);
  if (!weeks.length) return "";
  const actorFor = (kind, weekStart) =>
    (events || [])
      .filter((event) => event.kind === kind && event.date === weekStart)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.actorName ?? null;
  const when = (value) => (value ? moment(value).format("MMM D, YYYY h:mm A") : "");
  const lines = weeks.map((week) => {
    const sub = week.submission;
    const label = `Week of ${moment(week.weekStart).format("MMM D")} – ${moment(week.weekEnd).format("MMM D")}`;
    if (week.status === "OPEN") {
      // The week in progress owes nothing yet; only a finished week is
      // printed as outstanding.
      return week.current
        ? `<span class="signoff-note">${escapeHtml(label)}: in progress</span>`
        : `<span class="signoff-open">${escapeHtml(label)}: not submitted</span>`;
    }
    const byOffice = sub.submittedById != null && sub.submittedById !== technician.technicianId;
    const who = byOffice
      ? `by ${escapeHtml(actorFor("TIMECARD_SUBMITTED", week.weekStart) ?? "the office")} on the technician's behalf${sub.submitReason ? ` (${escapeHtml(sub.submitReason)})` : ""}`
      : `by ${escapeHtml(technician.technician)}`;
    let text = `${escapeHtml(label)}: submitted ${when(sub.submittedAt)} ${who}`;
    if (week.status === "REOPEN_REQUESTED") text += ` · reopen requested ${when(sub.requestedAt)}`;
    if (week.status === "REOPENED") text += ` · reopened ${when(sub.decidedAt)}, not yet resubmitted`;
    if (week.current && week.status !== "REOPENED") text += " · signed before the week ended";
    if (sub.changedAfterSubmitAt && week.status !== "REOPENED") text += ` · changed by the office after sign-off ${when(sub.changedAfterSubmitAt)}`;
    return `<span class="${week.status === "SUBMITTED" && !sub.changedAfterSubmitAt ? "signoff-ok" : "signoff-note"}">${text}</span>`;
  });
  return `<div class="signoff">${lines.map((line) => `<p>${line}</p>`).join("")}</div>`;
};

const sheetFor = (technician, range, events, onCall, signoff) => {
  const branches = branchesFor(technician);
  const rows = breakdownRows(technician);
  const caveats = caveatsFor(technician);

  // Days on call within the range. A pill under the date, as on screen: a
  // fact about the day, not hours, so no figure in any column.
  const onCallDays = onCallDaysInRange(onCall, range);
  const onCallSet = new Set(onCallDays);

  // Entries and on-call days in one date order, each day visited once. The
  // date is written on a day's first row only; a rail down the left of every
  // row of the day, alternating tone day to day, groups the rest beneath it.
  // Rows with no date sort last.
  const dayOf = (row) => (row.date ? moment(row.date).format("YYYY-MM-DD") : "");
  const days = [...new Set([...rows.map(dayOf), ...onCallDays])].sort((a, b) =>
    a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)
  );

  const rowClass = (dayIndex, first) => `d${dayIndex % 2}${first ? " first" : ""}`;
  const dateCell = (day, first, onCallDay) =>
    first
      ? `<td class="date"><span class="day">${day ? escapeHtml(displayDay(day)) : "&mdash;"}</span>${
          onCallDay ? `<span class="oncall-chip">On call</span>` : ""
        }</td>`
      : `<td class="date"></td>`;

  const entryRow = (row, dayIndex, first, onCallDay) => {
    const line = `
        <tr class="${rowClass(dayIndex, first)}">
          ${dateCell(dayOf(row), first, onCallDay)}
          <td class="wo">${cell(row.woNumber)}</td>
          <td class="customer">${cell(row.customer)}</td>
          <td class="branch">${cell(row.branch)}</td>
          <td class="num">${cell(row.regularTime)}${row.regularSpan ? `<span class="span">${escapeHtml(row.regularSpan)}</span>` : ""}</td>
          <td class="num">${cell(row.travelTime)}${row.travelSpan ? `<span class="span">${escapeHtml(row.travelSpan)}</span>` : ""}</td>
          <td class="num">${cell(row.overTime)}</td>
          <td class="num">${cell(row.doubleTime)}</td>
        </tr>`;

    // The lunch sits under the span it interrupted, as it does on screen —
    // where it falls is what decides which job earned the overtime, so
    // parking it at the end of the day would lose the explanation. Laid out
    // like an entry: the label in the WO column, the deduction over its
    // clock span in the Regular column.
    if (row.lunchTime) {
      return `${line}
        <tr class="lunch ${rowClass(dayIndex, false)}">
          <td class="date"></td>
          <td class="wo lunch-label">Lunch</td>
          <td class="customer"></td>
          <td class="branch"></td>
          <td class="num lunch-num">-${escapeHtml(row.lunchTime)}${row.lunchSpan ? `<span class="span lunch-span">${escapeHtml(row.lunchSpan)}</span>` : ""}</td>
          <td class="num"></td>
          <td class="num"></td>
          <td class="num"></td>
        </tr>`;
    }
    // No deduction, but accounted for. On a sheet filed in a cabinet the
    // reason has to travel with the day, since nobody can ask later.
    if (row.lunchNote) {
      return `${line}
        <tr class="lunch ${rowClass(dayIndex, false)}">
          <td class="date"></td>
          <td class="wo lunch-label">Lunch</td>
          <td colspan="6" class="lunch-note">${escapeHtml(row.lunchNote)}</td>
        </tr>`;
    }
    return line;
  };

  const bodyRows = days
    .map((day, dayIndex) => {
      const onCallDay = onCallSet.has(day);
      // By first punch, as on screen: the day reads top to bottom in the
      // order it was worked. The breakdown rows carry spans as text, so the
      // clock is read off the source rows, which line up index for index.
      const own = byFirstPunch(
        rows.flatMap((row, index) => (dayOf(row) === day ? [index] : [])),
        (index) => technician.rows[index]
      );
      // A day on call with nothing worked still needs a row to carry the date.
      if (!own.length) {
        return `
        <tr class="${rowClass(dayIndex, true)}">
          ${dateCell(day, true, onCallDay)}
          <td colspan="7" class="no-hours">No hours</td>
        </tr>`;
      }
      return own.map((index, position) => entryRow(rows[index], dayIndex, position === 0, onCallDay)).join("");
    })
    .join("");

  const notes = caveats.length
    ? `<div class="notes">${caveats.map((note) => `<p>${escapeHtml(note)}</p>`).join("")}</div>`
    : "";

  return `
    <section class="sheet" data-filename="${escapeHtml(fileTitleFor(technician, range))}">
      <!-- The paper margin lives in this table's header and footer rows:
           a table's header and footer repeat on every printed page, where
           padding on the sheet applies once and left the second page of a
           long card starting at the paper's edge. -->
      <table class="page">
        <thead><tr><td class="page-top"></td></tr></thead>
        <tfoot><tr><td class="page-bottom"></td></tr></tfoot>
        <tbody><tr><td class="page-body">
      <header>
        <div>
          <h1>${escapeHtml(technician.technician)}</h1>
          <p class="meta">${escapeHtml(branches.join(", ") || "No branch recorded")}</p>
        </div>
        <div class="right">
          <p class="range">${escapeHtml(rangeLabel(range))}</p>
          <p class="meta">${
            isManualEntry(technician)
              ? "Manual entry — hours as recorded"
              : hasOvertimeRule(technician)
              ? escapeHtml(
                  ALL_PAY_RULES.filter((rule) => isRuleOn(technician, rule))
                    .map((rule) => rule.label)
                    .join(" · "),
                )
              : "No overtime policy assigned"
          }</p>
          ${
            onCallDays.length
              ? `<p class="meta"><span class="oncall-chip">On call ${onCallDays.length} ${
                  onCallDays.length === 1 ? "day" : "days"
                }</span></p>`
              : ""
          }
        </div>
      </header>

      ${signoffBlock(technician, range, signoff, events)}

      <table class="hours">
        <thead>
          <tr>
            <th class="date" style="text-align:center">Date</th>
            <th class="wo">${escapeHtml(vocabularize("WO #"))}</th>
            <th class="customer">Customer</th>
            <th class="branch">Branch</th>
            <th class="num wide">Regular</th>
            <th class="num wide">Travel</th>
            <th class="num">Overtime</th>
            <th class="num">Double</th>
          </tr>
        </thead>
        <tbody>${bodyRows}</tbody>
        <tfoot>
          <tr>
            <td colspan="4">Total</td>
            <td class="num">${formatHhMm(technician.regularMinutes)}</td>
            <td class="num">${formatHhMm(technician.travelMinutes)}</td>
            <td class="num">${formatHhMm(technician.overMinutes)}</td>
            <td class="num">${formatHhMm(technician.doubleMinutes ?? 0)}</td>
          </tr>
        </tfoot>
      </table>

      <!-- Hours per pay rate, which is how payroll keys them. Travel is paid
           at the regular rate and counts toward the threshold, so it belongs
           with the regular hours, not beside them. -->
      <div class="pay">
        <div class="pay-row pay-main">
          <span class="pay-label">Regular rate</span>
          <span class="pay-value">${formatHhMm(technician.regularMinutes + technician.travelMinutes)}</span>
          <span class="pay-note">${formatHhMm(technician.regularMinutes)} worked + ${formatHhMm(technician.travelMinutes)} travel</span>
        </div>
        <div class="pay-row">
          <span class="pay-label">Overtime (1.5&times;)</span>
          <span class="pay-value">${formatHhMm(technician.overMinutes)}</span>
          <span class="pay-note"></span>
        </div>
        <div class="pay-row">
          <span class="pay-label">Double time (2&times;)</span>
          <span class="pay-value">${formatHhMm(technician.doubleMinutes ?? 0)}</span>
          <span class="pay-note"></span>
        </div>
        <div class="pay-row pay-total">
          <span class="pay-label">Total hours</span>
          <span class="pay-value">${formatHhMm(technician.totalMinutes)}</span>
          <span class="pay-note">after lunch</span>
        </div>
      </div>

      ${notes}

      ${changesBlock(events, range)}

      <footer>
        <span>${escapeHtml(technician.technician)}</span>
        <span>Printed ${moment().format("MMM D, YYYY")}</span>
      </footer>
        </td></tr></tbody>
      </table>
    </section>`;
};

const STYLES = `
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: Inter, "Helvetica Neue", Arial, sans-serif;
    color: #1F2A44;
    background: #F1F5F9;
  }

  /* Screen-only chrome. The document opens in a window the user can read
     before committing paper to it, so the toolbar explains what will print. */
  .toolbar {
    position: sticky; top: 0; z-index: 2;
    display: flex; align-items: center; gap: 12px;
    padding: 12px 16px;
    background: #1F2A44; color: #fff;
    font-size: 13px;
  }
  .toolbar button {
    font: inherit; font-weight: 600;
    padding: 7px 14px; border: 0; border-radius: 8px;
    background: #0065FD; color: #fff; cursor: pointer;
  }
  .toolbar span { opacity: 0.85; }

  .sheet {
    background: #fff;
    /* Letter width with the same 14mm margins the paper gets, so the
       on-screen preview is the paper: a table that fits one fits the other. */
    box-sizing: border-box;
    width: 216mm;
    margin: 16px auto;
    padding: 14mm;
    box-shadow: 0 2px 12px rgba(16, 24, 40, 0.14);
  }

  header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
  header .right { text-align: right; }
  h1 { margin: 0; font-size: 19px; letter-spacing: -0.2px; }
  .range { margin: 0; font-size: 13px; font-weight: 600; }
  .meta { margin: 3px 0 0; font-size: 12px; color: #64748B; }

  table:not(.page) { width: 100%; border-collapse: collapse; margin-top: 14px; }
  /* Fixed layout: the figure columns are sized for their widest span and
     the customer column takes whatever is left, and wraps when it runs long.
     Under automatic layout the fixed minimums added up to more than the
     printable width and the table ran past the right margin. */
  .hours { table-layout: fixed; }
  .hours th.date { width: 84px; }
  .hours th.wo { width: 58px; }
  .hours th.branch { width: 70px; }
  /* Centred like the date, header and cells alike, so a WO number and the
     Lunch label beneath it line up — the screen centres these columns too. */
  .hours .wo, .hours .branch { text-align: center; }
  .hours th.num { width: 70px; }
  .hours th.num.wide { width: 100px; }
  th, td { padding: 6px 6px; font-size: 12px; text-align: left; }
  thead th {
    border-bottom: 1.5px solid #1F2A44;
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.6px; color: #45556C;
  }
  tbody td { border-bottom: 1px solid #E2E8F0; }
  /* Centred, header and figure alike, so a figure and the clock span under
     it sit directly beneath their label — as the results table on screen. */
  .num { text-align: center; white-space: nowrap; font-variant-numeric: tabular-nums; }
  /* One line, always: the column is sized for the longest span. */
  .span { display: block; margin-top: 1px; font-size: 9px; font-weight: 400; color: #64748B; white-space: nowrap; line-height: 1.2; letter-spacing: -0.01em; }
  /* Work order, branch and customer wrap rather than being cut off: a printed
     timecard is read by someone who cannot hover for the rest of the name, so
     the row grows to hold it. A long unbroken work order number breaks
     anywhere rather than pushing into the next column. */
  .date { white-space: nowrap; }
  .wo, .customer, .branch { white-space: normal; overflow-wrap: anywhere; word-break: break-word; }

  /* One day's rows share a rail down the left, alternating tone day to day,
     with a heavier rule where a new day begins: the bracket that tells one
     day's rows from the next's, the way the screen groups them. The date is
     written once, on the day's first row. */
  tbody tr.d0 > td:first-child { border-left: 3px solid #1F2A44; }
  tbody tr.d1 > td:first-child { border-left: 3px solid #94A3B8; }
  tbody tr.first > td { border-top: 2px solid #CBD5E1; }
  td.date { text-align: center; vertical-align: middle; }
  td.date .day { display: block; font-weight: 600; color: #1F2A44; }
  td.date .oncall-chip { margin: 2px 0 0; font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; }
  .no-hours { color: #94A3B8; }

  /* Lunch: an entry-shaped row with no tint, the label in the WO column and
     the deduction over its clock span in the Regular column. */
  tbody tr.lunch td { font-size: 11px; padding-top: 4px; padding-bottom: 4px; }
  .lunch-label { text-align: center; font-weight: 600; color: #92400E; }
  .lunch-num { font-weight: 500; color: #92400E; }
  .lunch-span { color: #B45309; }
  .lunch-note { color: #92400E; }
  .oncall-chip { display: inline-block; padding: 1px 7px; border-radius: 5px; background: #EDE9FE; color: #5B21B6; font-weight: 600; font-size: 10.5px; line-height: 15px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  tfoot td { border-top: 1.5px solid #1F2A44; font-weight: 700; font-size: 12.5px; padding-top: 8px; }

  .pay { margin: 12px 0 0; width: 440px; border: 1px solid #CBD5E1; border-radius: 6px; overflow: hidden; }
  .pay-row { display: grid; grid-template-columns: 130px 64px 1fr; align-items: baseline; gap: 10px; padding: 5px 10px; font-size: 11.5px; border-top: 1px solid #E5E8F3; white-space: nowrap; }
  .pay-row:first-child { border-top: 0; }
  .pay-label { color: #1A2332; }
  .pay-value { text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; color: #1A2332; }
  .pay-note { font-size: 10px; color: #64748B; }
  .pay-main { background: #EEF4FF; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .pay-main .pay-label { font-weight: 700; color: #0058E5; }
  .pay-main .pay-value { font-size: 15px; color: #0058E5; }
  .pay-total { background: #F1F5F9; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .pay-total .pay-label { font-weight: 700; }

  .signoff { margin-top: 10px; }
  .signoff p { margin: 0 0 2px; font-size: 11px; line-height: 1.4; }
  .signoff-ok { color: #166534; }
  .signoff-note { color: #92400E; }
  .signoff-open { color: #991B1B; font-weight: 600; }
  .notes { margin-top: 12px; padding: 8px 10px; border-left: 3px solid #FDE68A; background: #FFFBEB; }
  .notes p { margin: 0 0 4px; font-size: 10.5px; line-height: 1.45; color: #92400E; }
  .notes p:last-child { margin-bottom: 0; }
  .changes { margin-top: 14px; }
  .changes h2 { margin: 0 0 6px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #5F6477; }
  .changes-table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  .changes-table th { text-align: left; font-size: 9.5px; letter-spacing: 0.04em; text-transform: uppercase; color: #868DA6; padding: 3px 6px; border-bottom: 1px solid #E5E8F3; white-space: nowrap; }
  .changes-table td { padding: 5px 6px; border-bottom: 1px solid #F1F5F9; vertical-align: top; color: #1A2332; }
  .changes-table td.date, .changes-table td.wo, .changes-table td.by { white-space: nowrap; }
  .changes-table td.reason { min-width: 150px; }
  .changes-table td.reason { color: #5F6477; font-style: italic; }
  .changes-table .when { color: #868DA6; font-style: normal; }

  footer {
    display: flex; justify-content: space-between;
    margin-top: 16px; padding-top: 6px; border-top: 1px solid #E2E8F0;
    font-size: 10px; color: #94A3B8;
  }

  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet {
      /* The page margin is carried here rather than on @page: the browser
         prints its own header and footer (date, title, "about:blank", page
         count) into the @page margin band, and a zero band leaves it nowhere
         to put them. */
      width: auto; margin: 0; padding: 0 14mm;
      box-shadow: none;
      /* One employee per page. The last sheet takes no break after it, or the
         job ends on a blank sheet of paper every time. */
      break-after: page;
      page-break-after: always;
    }
    .sheet:last-child { break-after: auto; page-break-after: auto; }
    /* Printing each technician on their own: only the current sheet goes to
       paper, and it takes no break after it, or every file would end on a
       blank page. */
    body.solo .sheet { display: none; }
    body.solo .sheet.current { display: block; break-after: auto; page-break-after: auto; }
    /* A technician with more entries than fit on one page keeps the column
       headings on the continuation, and no row is split down the middle. */
    thead { display: table-header-group; }
    table:not(.page) tr { break-inside: avoid; page-break-inside: avoid; }
    .page > tbody > tr { break-inside: auto; page-break-inside: auto; }
  }

  .page { width: 100%; border-collapse: collapse; }
  /* Child selectors only: a descendant selector here reached every cell of
     the tables inside the wrapper and stripped their padding and borders. */
  .page > thead > tr > td, .page > tfoot > tr > td, .page > tbody > tr > td { padding: 0; border: 0; }
  .page-top, .page-bottom { height: 0; }
  @media print {
    .page-top, .page-bottom { height: 14mm; }
    .page thead { display: table-header-group; }
    .page tfoot { display: table-footer-group; }
  }
  @page { margin: 0; }
`;

/**
 * Open the printable document for the given technicians.
 *
 * Returns false when the browser refused the window — a popup blocker is the
 * usual reason, and the caller says so rather than leaving a button that looks
 * broken when clicked.
 */
// `historyByUser` is the page's prefetched hours history, keyed by user id —
// the same map the History drawer opens from — so the sheet needs no request
// of its own.
export const openPrintTimecards = (technicians, range, historyByUser = {}, onCallByUser = {}, signoffByUser = {}) => {
  if (!technicians.length) return true;

  const win = window.open("", "_blank");
  if (!win) return false;

  const count = technicians.length;
  // One technician: the page is titled for them, so Save as PDF is named for
  // them. Several: the combined document gets a range title, and "Print each
  // separately" titles the page for each person in turn.
  const title =
    count === 1 ? fileTitleFor(technicians[0], range) : `Timecards - ${rangeLabel(range)}`;
  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>${STYLES}</style>
    <script>
      // One print dialog per technician, each with the page titled for that
      // person, so "Save as PDF" names each file after them and a printer
      // gets one job per person. window.print() waits for its dialog to
      // close before returning, which is what lets this run in sequence.
      function printEach() {
        var sheets = Array.prototype.slice.call(document.querySelectorAll(".sheet"));
        var original = document.title;
        document.body.classList.add("solo");
        try {
          sheets.forEach(function (sheet) {
            sheets.forEach(function (other) { other.classList.toggle("current", other === sheet); });
            document.title = sheet.getAttribute("data-filename") || original;
            window.print();
          });
        } finally {
          document.body.classList.remove("solo");
          sheets.forEach(function (other) { other.classList.remove("current"); });
          document.title = original;
        }
      }
    </script>
  </head>
  <body>
    <div class="toolbar">
      ${
        count === 1
          ? `<button type="button" onclick="window.print()">Print</button>
      <span>Timecard — ${escapeHtml(rangeLabel(range))}</span>`
          : `<button type="button" onclick="printEach()">Print each separately</button>
      <button type="button" onclick="window.print()" style="background:#fff;color:#1F2A44">Print all as one</button>
      <span>${count} timecards — ${escapeHtml(rangeLabel(range))}. Each separately: one dialog per technician, each PDF named for them.</span>`
      }
    </div>
    ${technicians
      .map((technician) =>
        sheetFor(
          technician,
          range,
          historyByUser[technician.technicianId],
          onCallByUser[technician.technicianId],
          signoffByUser[technician.technicianId]
        )
      )
      .join("")}
  </body>
</html>`;

  win.document.write(html);
  win.document.close();

  // Printed from here rather than from a script inside the document. The page
  // pulls in nothing external — no images, and the font stack falls back to
  // what the system already has — so it is fully laid out by the time
  // document.close() returns and there is nothing to wait for.
  //
  // Wrapped because a browser that refuses the dialog should still leave a
  // readable document and its own Print button behind rather than throwing.
  // Straight to the dialog for one technician. For several, the window
  // waits for a choice between one combined document and one per person.
  if (count === 1) {
    try {
      win.focus();
      win.print();
    } catch (error) {
      // Left to the toolbar button in the opened window.
    }
  } else {
    win.focus();
  }
  return true;
};
